
import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { submitToZoho } from "@/utils/secureFormSubmission";

const AuraXPSection = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRequestDemo = () => {
    window.open('https://outlook.office.com/bookwithme/user/3c14c6b9047b45dfb64e4bc9045f29dd@spharaka.com/meetingtype/p0-SnGKD_EqEPlSvi_wdNg2?anonymous&ismsaljsauthenabled&ep=mlink', '_blank');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const success = await submitToZoho({
        ...formData,
        message: `AuraXP Information Request: ${formData.message}`,
        formType: "AuraXP Learn More"
      });

      if (success) {
        toast.success("Thank you! We'll send you detailed AuraXP information shortly.");
        setFormData({ firstName: '', lastName: '', email: '', company: '', message: '' });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      toast.error("Request failed. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 md:py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-4xl mx-auto">
          <Badge className="mb-4 md:mb-6 bg-[#8932c8] text-white px-3 py-1 text-sm">
            Meet AuraXP
          </Badge>
          
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-5 leading-tight">
            Your <span className="text-[#8932c8]">Autonomous AI</span> Cyber Defense Agent
          </h2>
          
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 mb-5 md:mb-7 leading-relaxed max-w-3xl mx-auto">
            AuraXP™ (Autonomous Unified Response Agent) is Spharaka's revolutionary Agentic AI that doesn't just detect threats - it thinks, learns, and responds autonomously. Experience the future of cybersecurity with AI that operates like your most skilled security analyst, 24/7.
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
            <div className="bg-white p-3 md:p-5 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-base md:text-lg font-bold mb-2 text-[#8932c8]">Autonomous Detection</h3>
              <p className="text-xs md:text-sm text-gray-600 leading-relaxed">Identifies and analyzes threats without human intervention using advanced behavioral analytics.</p>
            </div>
            
            <div className="bg-white p-3 md:p-5 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-base md:text-lg font-bold mb-2 text-[#8932c8]">Intelligent Response</h3>
              <p className="text-xs md:text-sm text-gray-600 leading-relaxed">Makes split-second decisions and executes containment actions faster than any human analyst.</p>
            </div>
            
            <div className="bg-white p-3 md:p-5 rounded-xl shadow-sm border border-gray-100 sm:col-span-2 lg:col-span-1">
              <h3 className="text-base md:text-lg font-bold mb-2 text-[#8932c8]">Continuous Learning</h3>
              <p className="text-xs md:text-sm text-gray-600 leading-relaxed">Evolves and improves its defense strategies by learning from every interaction and threat.</p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
            <Button 
              onClick={handleRequestDemo}
              className="bg-[#8932c8] hover:bg-[#7a2cb3] text-white px-6 md:px-8 py-3 md:py-4 text-sm md:text-base w-full sm:w-auto"
            >
              Talk to a Spharaka XDR™ Expert
            </Button>
            
            <Dialog>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="border-[#8932c8] text-[#8932c8] hover:bg-[#8932c8] hover:text-white px-6 md:px-8 py-3 md:py-4 text-sm md:text-base w-full sm:w-auto"
                >
                  Learn More About AuraXP
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold">Get AuraXP Information</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      name="firstName"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                    <Input
                      name="lastName"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  <Input
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleChange}
                    required
                  />
                  <Textarea
                    name="message"
                    placeholder="What specific AuraXP capabilities interest you?"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-[#8932c8] hover:bg-[#7a2cb3]"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Send Me AuraXP Information"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AuraXPSection;
